/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : DescriptionList
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical key/value display for detail panels — replaces the
 *                  ad-hoc `<dl>` / two-column grids that vary page to page.
 *                  Responsive: stacks on mobile, label/value columns on ≥sm.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { clsx } from 'clsx';

export interface DescriptionItem {
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface DescriptionListProps {
  items: DescriptionItem[];
  /** Number of value columns on ≥md (1 or 2). Default 1. */
  columns?: 1 | 2;
  className?: string;
}

/** Canonical detail key/value list. */
export const DescriptionList: React.FC<DescriptionListProps> = ({ items, columns = 1, className }) => (
  <dl className={clsx('grid gap-x-8 gap-y-3', columns === 2 ? 'md:grid-cols-2' : 'grid-cols-1', className)}>
    {items.map((it, i) => (
      <div key={i} className="flex flex-col gap-0.5 border-b border-[var(--color-border)] pb-2 last:border-0 sm:flex-row sm:items-baseline sm:gap-3 sm:border-0 sm:pb-0">
        <dt className="text-[length:var(--text-sm)] font-medium text-[var(--color-text-secondary)] sm:w-40 sm:flex-shrink-0">
          {it.label}
        </dt>
        <dd className="min-w-0 break-words text-[length:var(--text-body)] text-[var(--color-text-primary)]">{it.value}</dd>
      </div>
    ))}
  </dl>
);

export default DescriptionList;
